import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Mail,
  RefreshCw,
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  User,
} from "lucide-react";

/**
 * SentEmailLog.jsx — BM-H548, 16 September 2026 (Vanessa).
 *
 * "we need a sent items history that covers all emails sent via the portal".
 *
 * Reads GET /email-audit/recent, which serves the ``email_audit`` collection.
 * As of BM-H548 every send goes through the one wrapper in
 * hub/email_utils.send_email and that wrapper writes the row, so this page is
 * the whole outbound history rather than the subset of senders that happened
 * to log themselves. Rows sent before BM-H548 deployed only appear if their
 * call site logged them, which is why the empty-state says so.
 *
 * Read-only, staff-wide (the route is session-sealed). Filters run client side
 * over the fetched window so typing does not re-hit the API.
 */

const WINDOW_OPTIONS = [
  { days: 1, label: "24 hours" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const STATUS_CONFIG = {
  sent: {
    label: "Sent",
    icon: CheckCircle2,
    color: "bg-status-green-dim text-status-green border-green-200",
  },
  failed: {
    label: "Failed",
    icon: XCircle,
    color: "bg-status-red-dim text-status-red border-red-200",
  },
  error: {
    label: "Failed",
    icon: XCircle,
    color: "bg-status-red-dim text-status-red border-red-200",
  },
  disabled: {
    label: "Not sent (email off)",
    icon: AlertTriangle,
    color: "bg-status-amber-dim text-status-amber border-amber-200",
  },
  attempt: {
    label: "Attempted",
    icon: Clock,
    color: "bg-sky-50 text-sky-700 border-sky-200",
  },
  queued: {
    label: "Queued",
    icon: Clock,
    color: "bg-sky-50 text-sky-700 border-sky-200",
  },
  refused: {
    label: "Refused",
    icon: XCircle,
    color: "bg-status-red-dim text-status-red border-red-200",
  },
};

const UNCLASSIFIED = "unclassified_portal_send";

export function templateLabel(key) {
  // An unlabelled send is named honestly rather than hidden: before BM-H548
  // most of these were recorded nowhere at all.
  if (!key || key === UNCLASSIFIED || key === "unknown") return "Other portal email";
  const words = String(key).replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function formatWhen(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return (
    d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

/** Rows the filters keep. Exported so the behaviour is testable without a DOM. */
export function filterRows(rows, { query, mine, myEmail, statusFilter }) {
  const q = (query || "").trim().toLowerCase();
  const me = (myEmail || "").trim().toLowerCase();
  return (rows || []).filter((r) => {
    const status = r.status || "";
    if (statusFilter) {
      // "Failed" has to mean failed however the transport phrased it, or a
      // send that never left would sit in the log looking fine.
      const wanted =
        statusFilter === "failed"
          ? ["failed", "error", "refused"]
          : [statusFilter];
      if (!wanted.includes(status)) return false;
    }
    // "Sent as me" with no known signed-in address would silently blank the
    // page, so it falls back to showing everything.
    if (mine && me && (r.from_address || "").toLowerCase() !== me) return false;
    if (!q) return true;
    const haystack = [
      r.subject,
      r.from_address,
      r.matter_reference,
      r.template_key,
      ...(r.to_addresses || []),
      ...(r.cc_addresses || []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

function currentUserEmail() {
  // Same resolution order as CreditControlRecord so "sent as me" agrees with
  // the mailbox a chaser actually goes out from.
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return (
      user.user_email ||
      user.email ||
      user.ms_email ||
      localStorage.getItem("bonsai_user_email") ||
      ""
    ).toLowerCase();
  } catch {
    return (localStorage.getItem("bonsai_user_email") || "").toLowerCase();
  }
}

export default function SentEmailLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [days, setDays] = useState(7);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [mine, setMine] = useState(false);
  const myEmail = currentUserEmail();

  const fetchRows = useCallback(async (windowDays) => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(`${API}/email-audit/recent`, {
        params: { days: windowDays, direction: "sent", limit: 2000 },
      });
      setRows(res.data?.rows || []);
    } catch (e) {
      const msg = errorMessage(e, "Could not load the sent email history.");
      setLoadError(msg);
      setRows([]);
      if (typeof window !== "undefined" && window.errorMessage) {
        window.errorMessage(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows(days);
  }, [days, fetchRows]);

  const visible = useMemo(
    () => filterRows(rows, { query, mine, myEmail, statusFilter }),
    [rows, query, mine, myEmail, statusFilter],
  );

  const counts = useMemo(() => {
    const out = { sent: 0, failed: 0, other: 0 };
    for (const r of rows) {
      if (r.status === "sent") out.sent += 1;
      else if (r.status === "failed" || r.status === "error" || r.status === "refused")
        out.failed += 1;
      else out.other += 1;
    }
    return out;
  }, [rows]);

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="sent-email-log">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Sent Email Log
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Every email the portal sends, whichever screen sent it and whoever it
            went out as. Emails sent by hand from Outlook are not here, they are
            in your own Sent Items.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchRows(days)}
          disabled={loading}
          data-testid="sent-log-refresh"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap my-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search subject, recipient, sender, matter"
            className="pl-8 w-[340px]"
            data-testid="sent-log-search"
          />
        </div>

        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
          data-testid="sent-log-window"
          aria-label="Time window"
        >
          {WINDOW_OPTIONS.map((o) => (
            <option key={o.days} value={o.days}>
              Last {o.label}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
          data-testid="sent-log-status"
          aria-label="Status"
        >
          <option value="">All outcomes</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="disabled">Not sent (email off)</option>
          <option value="attempt">Attempted</option>
        </select>

        <Button
          variant={mine ? "default" : "outline"}
          onClick={() => setMine((v) => !v)}
          disabled={!myEmail}
          title={
            myEmail
              ? "Only emails sent from your mailbox"
              : "We do not know which mailbox is yours in this session"
          }
          data-testid="sent-log-mine"
        >
          <User className="h-4 w-4 mr-2" />
          Sent as me
        </Button>

        <div className="text-sm text-slate-500 ml-auto" data-testid="sent-log-counts">
          {visible.length} of {rows.length} shown · {counts.sent} sent ·{" "}
          {counts.failed} failed
        </div>
      </div>

      {loadError ? (
        <div
          className="border border-red-200 bg-status-red-dim text-status-red rounded-md p-4 text-sm"
          data-testid="sent-log-error"
        >
          {loadError}
        </div>
      ) : null}

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left font-medium px-3 py-2 w-[130px]">Sent</th>
              <th className="text-left font-medium px-3 py-2 w-[210px]">From</th>
              <th className="text-left font-medium px-3 py-2 w-[230px]">To</th>
              <th className="text-left font-medium px-3 py-2">Subject</th>
              <th className="text-left font-medium px-3 py-2 w-[170px]">Matter</th>
              <th className="text-left font-medium px-3 py-2 w-[170px]">Kind</th>
              <th className="text-left font-medium px-3 py-2 w-[150px]">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                  Loading
                </td>
              </tr>
            ) : null}

            {!loading && visible.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-10 text-center text-slate-500"
                  data-testid="sent-log-empty"
                >
                  Nothing in this window. Emails sent before the universal send
                  log went live only appear here if the screen that sent them
                  logged them itself.
                </td>
              </tr>
            ) : null}

            {visible.map((r, i) => {
              const cfg = STATUS_CONFIG[r.status] || {
                label: r.status || "Unknown",
                icon: Clock,
                color: "bg-slate-50 text-slate-500 border-slate-200",
              };
              const Icon = cfg.icon;
              return (
                <tr
                  key={r.message_id || `${r.sent_at}-${i}`}
                  className="border-t border-slate-100 align-top"
                  data-testid="sent-log-row"
                >
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                    {formatWhen(r.sent_at)}
                  </td>
                  <td className="px-3 py-2 break-all" data-testid="sent-log-from">
                    {r.from_address || "—"}
                  </td>
                  <td className="px-3 py-2 break-all">
                    {(r.to_addresses || []).join(", ") || "—"}
                    {(r.cc_addresses || []).length ? (
                      <div className="text-xs text-slate-500">
                        cc {r.cc_addresses.join(", ")}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    {r.subject || "(no subject)"}
                    {r.error ? (
                      <div className="text-xs text-status-red mt-0.5">{r.error}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.matter_reference ? (
                      <Link
                        className="text-blue-700 hover:underline"
                        to={`/matter/${encodeURIComponent(r.matter_reference)}`}
                      >
                        {r.matter_reference}
                      </Link>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {templateLabel(r.template_key)}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${cfg.color}`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {cfg.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
